import { ChangeEvent, useEffect, useRef, useState } from 'react';

import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Divider,
  Modal,
  useTheme,
  InputBase,
  Typography,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  LinearProgress,
} from '@mui/material';
import type { InputBaseProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import { User as UserIcon } from 'react-feather';
import { useNavigate } from 'react-router-dom';

import { useLocationContext } from '~/contexts/location.context';
import { getRoutePath } from '~/router/routes';
import { useGetMembersByNameQuery } from '~/services/member';
import { array } from '~/modules/@shared/helpers/array';
import { MemberNotRegisteredByLocation } from '~/services/types';

export interface SearchModalProps {
  open: boolean;
  handleClose: () => void;
}

const SearchInput = styled(InputBase)<InputBaseProps>({
  flex: 1,
  color: 'inherit',
  pl: '1rem',
});

export default function SearchModal({ open, handleClose }: SearchModalProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { locationId } = useLocationContext();
  const [searchText, setSearchText] = useState('');

  const { data: members, isPending } = useGetMembersByNameQuery<MemberNotRegisteredByLocation>({ locationId, name: searchText });
  
  const inputRef = useRef<HTMLInputElement>(null);
  const focusOnInput = () => setTimeout(() => inputRef.current && inputRef.current.focus(), 100);

  useEffect(() => {
    setSearchText('');
    if (open) focusOnInput();
  }, [open]);

  return (
    <Modal open={open} onClose={handleClose} aria-labelledby="search-modal" aria-describedby="universal-search">
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'background.paper',
          borderRadius: theme.shape.borderRadius,
          width: '100%',
          maxWidth: '700px',
          color: 'text.primary',
          '&:focus-visible': {
            outline: 'none',
          },
        }}>
        <Box
          sx={{
            p: 2,
            position: 'relative',
            borderRadius: theme.shape.borderRadius,
            display: 'flex',
            alignItems: 'center',
          }}>
          <Box
            sx={{
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 1,
            }}>
            <SearchIcon />
          </Box>
          <SearchInput
            placeholder="Search…"
            value={searchText}
            onChange={(ev: ChangeEvent<HTMLInputElement>) => setSearchText(ev.target.value)}
            inputRef={inputRef}
          />
          <Button
            size="small"
            variant="outlined"
            sx={{ borderRadius: 2, height: 24, minWidth: 0 }}
            onClick={handleClose}>
            esc
          </Button>
        </Box>
        {!searchText ? null : isPending ? (
          <Box sx={{ px: 1 }}>
            <LinearProgress />
          </Box>
        ) : (
          <>
            <Divider />
            <Box sx={{ p: 2 }}>
              <Typography variant="caption">Contacts</Typography>
              {(Array.isArray(members) ? members : []).length > 0 ? (
                <List>
                  {array(members)?.map((member) => (
                    <ListItem key={member.member_id} disablePadding>
                      <ListItemButton
                        onClick={() => {
                          navigate(getRoutePath('members.members.member', [member.member_id, 'attendance']));
                          handleClose();
                        }}>
                        <ListItemIcon sx={{ minWidth: 0, mr: 1 }}>
                          <UserIcon size={16} />
                        </ListItemIcon>
                        <ListItemText primary={member.member_name} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <List>
                  <ListItem disablePadding>
                    <ListItemText primary="No contacts found" />
                  </ListItem>
                </List>
              )}
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
}
